import React, { useState, useEffect } from "react";
import MobileWrapper from "../../components/MobileWrapper";
import { useSelector } from "react-redux";
import {
  FaClock,
  FaCalendarAlt,
  FaHistory,
  FaSignInAlt,
  FaSignOutAlt,
  FaUser,
  FaBuilding,
  FaSearch,
  FaTimesCircle,
  FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  createNewDocument,
  getAllDocsSnap,
  updateDocById,
} from "../../utils/firebase/Firebase.utils";
import ClockRecordCard from "./ClockRecordCard";

function ClockInOut() {
  const currentUser = useSelector((state) => state.userObject);
  const [clockRecords, setClockRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentClockInId, setCurrentClockInId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("today");
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  };

  // Get current time in HH:MM format
  const getCurrentTime = () => {
    const date = new Date();
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  useEffect(() => {
    // Fetch all clock records for the current user
    const fetchClockRecords = async () => {
      await getAllDocsSnap("clockRecords", (docs) => {
        // Filter records by current user's email
        const userRecords = docs.filter(
          (record) => record.userEmail === currentUser.email
        );

        // Sort by date and time in descending order
        userRecords.sort((a, b) => {
          const dateTimeA = new Date(
            `${a.date} ${a.clockInTime || a.clockOutTime}`
          );
          const dateTimeB = new Date(
            `${b.date} ${b.clockInTime || b.clockOutTime}`
          );
          return dateTimeB - dateTimeA;
        });

        setClockRecords(userRecords);
        setFilteredRecords(userRecords);

        // Check if user is already clocked in
        const todayDate = getCurrentDate();
        const activeClockIn = userRecords.find(
          (record) =>
            record.date === todayDate &&
            record.clockInTime &&
            !record.clockOutTime
        );

        if (activeClockIn) {
          setIsClockedIn(true);
          setCurrentClockInId(activeClockIn.id);
        } else {
          setIsClockedIn(false);
          setCurrentClockInId(null);
        }

        setIsLoading(false);
      });
    };

    fetchClockRecords();
  }, [currentUser.email]);

  // Apply search and filter to records
  useEffect(() => {
    let filtered = [...clockRecords];

    // Apply date filter
    if (activeFilter === "today") {
      const todayDate = getCurrentDate();
      filtered = filtered.filter((record) => record.date === todayDate);
    } else if (activeFilter === "week") {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.date);
        return recordDate >= oneWeekAgo;
      });
    } else if (activeFilter === "month") {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.date);
        return recordDate >= oneMonthAgo;
      });
    }

    // Apply search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((record) => {
        // Search in location
        if (record.location?.toLowerCase().includes(term)) return true;
        // Search in date
        if (record.date?.toLowerCase().includes(term)) return true;
        // Search in notes
        if (record.notes?.toLowerCase().includes(term)) return true;

        return false;
      });
    }

    setFilteredRecords(filtered);
  }, [searchTerm, clockRecords, activeFilter]);

  const handleClockIn = async () => {
    const currentDate = getCurrentDate();
    const currentTime = getCurrentTime();

    try {
      const newClockIn = {
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email,
        date: currentDate,
        clockInTime: currentTime,
        clockOutTime: null,
        location: currentUser.location || "Main Office",
        status: "active",
        notes: "",
        createdAt: new Date().toISOString(),
      };

      const docRef = await createNewDocument("clockRecords", newClockIn);
      setIsClockedIn(true);
      setCurrentClockInId(docRef.id);
    } catch (error) {
      console.error("Error clocking in:", error);
    }
  };

  const handleClockOutClick = () => {
    setShowConfirmation(true);
  };

  const confirmClockOut = async () => {
    if (!currentClockInId) return;

    const currentTime = getCurrentTime();

    try {
      await updateDocById("clockRecords", currentClockInId, {
        clockOutTime: currentTime,
        status: "completed",
        updatedAt: new Date().toISOString(),
      });

      setIsClockedIn(false);
      setCurrentClockInId(null);
    } catch (error) {
      console.error("Error clocking out:", error);
    } finally {
      setShowConfirmation(false);
    }
  };

  const cancelClockOut = () => {
    setShowConfirmation(false);
  };

  // Keep the existing handleClockOut but rename it
  const handleClockOut = confirmClockOut;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const options = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const calculateDuration = (clockInTime, clockOutTime) => {
    if (!clockInTime || !clockOutTime) return "In Progress";

    // Parse time values considering AM/PM format
    let inHours, inMinutes, outHours, outMinutes;

    // Check if time is in 12-hour format with AM/PM
    if (
      clockInTime.includes("AM") ||
      clockInTime.includes("PM") ||
      clockInTime.includes("am") ||
      clockInTime.includes("pm")
    ) {
      // Extract hours and minutes from times like "1:30 PM" or "01:30 PM"
      const inTimeParts = clockInTime.match(/(\d+):(\d+)\s*([AaPp][Mm])/);
      const outTimeParts = clockOutTime.match(/(\d+):(\d+)\s*([AaPp][Mm])/);

      if (!inTimeParts || !outTimeParts) return "Error calculating";

      // Convert to 24-hour format
      inHours = parseInt(inTimeParts[1]);
      inMinutes = parseInt(inTimeParts[2]);
      const inPeriod = inTimeParts[3].toUpperCase();

      outHours = parseInt(outTimeParts[1]);
      outMinutes = parseInt(outTimeParts[2]);
      const outPeriod = outTimeParts[3].toUpperCase();

      // Adjust hours for PM
      if (inPeriod === "PM" && inHours < 12) inHours += 12;
      if (inPeriod === "AM" && inHours === 12) inHours = 0;

      if (outPeriod === "PM" && outHours < 12) outHours += 12;
      if (outPeriod === "AM" && outHours === 12) outHours = 0;
    } else {
      // Handle 24-hour format
      const inParts = clockInTime.split(":").map(Number);
      const outParts = clockOutTime.split(":").map(Number);

      if (inParts.length < 2 || outParts.length < 2) return "Error calculating";

      inHours = inParts[0];
      inMinutes = inParts[1];
      outHours = outParts[0];
      outMinutes = outParts[1];
    }

    // Calculate the duration in minutes
    let durationMinutes =
      outHours * 60 + outMinutes - (inHours * 60 + inMinutes);

    // Handle overnight shifts
    if (durationMinutes < 0) durationMinutes += 24 * 60;

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  if (isLoading) {
    return (
      <MobileWrapper label="Clock In/Out">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            <p className="mt-2 text-sm text-[#363062]">
              Loading clock records...
            </p>
          </div>
        </div>
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper label="Clock In/Out">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 m-4 max-w-sm w-full">
              <div className="flex items-center justify-center text-red-500 mb-4">
                <FaExclamationTriangle className="text-2xl" />
              </div>
              <h3 className="text-lg font-medium text-center text-[#363062] mb-2">
                Confirm Clock Out
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to clock out? This action cannot be
                undone.
              </p>
              <div className="flex justify-between gap-3">
                <button
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none"
                  onClick={cancelClockOut}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 focus:outline-none"
                  onClick={confirmClockOut}
                >
                  Clock Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaClock className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Time Tracking
              </h1>
              <p className="text-xs text-gray-500">Manage your work hours</p>
            </div>
          </div>

          {/* Clock In/Out Button */}
          <button
            className={`flex items-center px-4 py-2 text-xs font-medium text-white rounded-md shadow-sm transition-colors ${
              isClockedIn
                ? "bg-red-500 hover:bg-red-600"
                : "bg-[#8FCD00] hover:bg-[#7db700]"
            }`}
            onClick={isClockedIn ? handleClockOutClick : handleClockIn}
          >
            {isClockedIn ? (
              <>
                <FaSignOutAlt className="w-3 h-3 mr-1.5" />
                Clock Out
              </>
            ) : (
              <>
                <FaSignInAlt className="w-3 h-3 mr-1.5" />
                Clock In
              </>
            )}
          </button>
        </div>

        {/* Current Status Card */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-sm font-medium text-[#363062]">
              Current Status
            </h2>
            <div
              className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                isClockedIn
                  ? "bg-[#8FCD00]/15 text-[#8FCD00]"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {isClockedIn ? "On Duty" : "Off Duty"}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <FaUser className="text-gray-400" />
              <span className="text-gray-700">
                {currentUser.displayName || currentUser.email}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <FaBuilding className="text-gray-400" />
              <span className="text-gray-700">
                {currentUser.location || "Main Office"}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <FaCalendarAlt className="text-gray-400" />
              <span className="text-gray-700">{getCurrentDate()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <FaClock className="text-gray-400" />
              <span className="text-gray-700">{getCurrentTime()}</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by date, location, or notes..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchTerm("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Time Period Filter */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "today"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => handleFilterChange("today")}
            >
              Today
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "week"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => handleFilterChange("week")}
            >
              This Week
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "month"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => handleFilterChange("month")}
            >
              This Month
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => handleFilterChange("all")}
            >
              All Records
            </button>
          </div>
        </div>

        {/* History Section Title */}
        <div className="flex items-center space-x-2 mb-3 mt-5">
          <FaHistory className="text-[#363062]" />
          <h2 className="text-sm font-medium text-[#363062]">Clock History</h2>
        </div>

        {/* Clock Records */}
        <div className="mt-2 space-y-3">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaHistory className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                No Clock Records Found
              </h3>
              <p className="text-xs text-gray-500">
                {clockRecords.length === 0
                  ? "You haven't clocked in or out yet"
                  : "Try adjusting your filters or search terms"}
              </p>
            </div>
          ) : (
            filteredRecords.map((record) => (
              <ClockRecordCard
                key={record.id}
                record={record}
                formatDate={formatDate}
                calculateDuration={calculateDuration}
              />
            ))
          )}
        </div>
      </div>
    </MobileWrapper>
  );
}

export default ClockInOut;
